import { format, startOfDay } from 'date-fns';
import { DATE_FORMAT_ISO_DAY } from '../../constants/dateFormat';
import { publicationsByDayKey, publicationsForDay, weekDays } from './functions';
import { CalendarToolbar } from './toolbar/CalendarToolbar';
import { MonthCalendarView } from './month/MonthCalendarView';
import { MonthDayListPopover } from './month/MonthDayListPopover';
import { WeekDayView } from './week/WeekDayView';
import { PublicationDetailPopover } from './PublicationDetailPopover';
import { usePublicationPreview } from './hooks/usePublicationPreview';
import type { CalendarDayLabel } from './calendarTemporalLabels';
import { CalendarGranularity, type CalendarPublication } from './types';

const ROOT_CLASS = 'flex w-full flex-col gap-4';

type PublicationsCalendarProps = Readonly<{
    anchorDate: Date;
    onAnchorChange: (next: Date) => void;
    granularity: CalendarGranularity;
    onGranularityChange: (next: CalendarGranularity) => void;
    publications: readonly CalendarPublication[];
    periodLabel: string;
    dayLabels: Readonly<Record<string, CalendarDayLabel>>;
}>;

export function PublicationsCalendar({
    anchorDate,
    onAnchorChange,
    granularity,
    onGranularityChange,
    publications,
    periodLabel,
    dayLabels,
}: PublicationsCalendarProps) {
    const preview = usePublicationPreview();
    const byDay = publicationsByDayKey(publications);

    return (
        <div className={ROOT_CLASS}>
            <CalendarToolbar
                anchorDate={anchorDate}
                onAnchorChange={onAnchorChange}
                granularity={granularity}
                onGranularityChange={onGranularityChange}
                periodLabel={periodLabel}
            />

            {granularity === CalendarGranularity.Month && (
                <MonthCalendarView
                    anchorDate={anchorDate}
                    byDay={byDay}
                    dayLabels={dayLabels}
                    onPublicationHoverStart={preview.schedulePublicationPreview}
                    onPublicationHoverEnd={preview.handlePublicationTriggerLeave}
                    onOpenDayList={preview.openDayList}
                />
            )}

            {granularity === CalendarGranularity.Week && (
                <WeekDayView
                    mode={CalendarGranularity.Week}
                    days={weekDays(anchorDate)}
                    publications={publications}
                    dayLabels={dayLabels}
                    onPublicationHoverStart={preview.schedulePublicationPreview}
                    onPublicationHoverEnd={preview.handlePublicationTriggerLeave}
                    onOpenDayList={preview.openDayList}
                />
            )}

            {granularity === CalendarGranularity.Day && (
                <WeekDayView
                    mode={CalendarGranularity.Day}
                    days={[startOfDay(anchorDate)]}
                    publications={publicationsForDay(publications, anchorDate)}
                    dayLabels={dayLabels}
                    onPublicationHoverStart={preview.schedulePublicationPreview}
                    onPublicationHoverEnd={preview.handlePublicationTriggerLeave}
                    onOpenDayList={preview.openDayList}
                />
            )}

            {preview.detailPopover ? (
                <PublicationDetailPopover
                    publication={preview.detailPopover.publication}
                    position={preview.detailPopover.position}
                    onClose={preview.closeDetailPopover}
                    onMouseEnter={preview.cancelClosePublicationPreview}
                    onMouseLeave={preview.schedulePublicationClose}
                />
            ) : null}

            {preview.dayPopover ? (
                <MonthDayListPopover
                    day={preview.dayPopover.day}
                    dayLabel={dayLabels[format(preview.dayPopover.day, DATE_FORMAT_ISO_DAY)]}
                    publications={publicationsForDay(publications, preview.dayPopover.day)}
                    position={preview.dayPopover.position}
                    onClose={preview.closeAll}
                    onPublicationHoverStart={preview.schedulePublicationPreview}
                    onPublicationHoverEnd={preview.handlePublicationTriggerLeave}
                    onPublicationTap={preview.openPublicationPreview}
                />
            ) : null}
        </div>
    );
}
